import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { platforms } from '@/mocks/platforms';
import { categories, categoryMap } from '@/mocks/categories';

export default function Category() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);

  const filteredPlatforms = useMemo(() => {
    if (activeSlug === 'all') return platforms;
    const categoryName = categoryMap[activeSlug];
    return platforms.filter(p => p.category === categoryName);
  }, [activeSlug]);

  const platformImages: Record<number, string> = {
    1: "https://readdy.ai/api/search-image?query=Modern%20clean%20streaming%20platform%20interface%20mockup%20on%20a%20laptop%20screen%20with%20movie%20thumbnails%2C%20warm%20coral%20accent%20colors%2C%20minimalist%20UI%20design%2C%20soft%20ambient%20lighting%2C%20professional%20product%20photography%2C%20simple%20background&width=800&height=500&seq=platform-card-01&orientation=landscape",
    2: "https://readdy.ai/api/search-image?query=Modern%20video%20streaming%20app%20interface%20on%20tablet%20device%20with%20movie%20posters%20grid%2C%20warm%20yellow%20amber%20accent%20colors%2C%20minimalist%20design%2C%20clean%20simple%20background%2C%20professional%20product%20photography&width=800&height=500&seq=platform-card-02&orientation=landscape",
    3: "https://readdy.ai/api/search-image?query=Modern%20video%20platform%20interface%20on%20smartphone%20showing%20drama%20series%20list%2C%20warm%20red%20accent%20colors%2C%20minimalist%20UI%20design%2C%20clean%20simple%20background%2C%20professional%20product%20photography&width=800&height=500&seq=platform-card-03&orientation=landscape",
    4: "https://readdy.ai/api/search-image?query=Modern%20anime%20streaming%20platform%20interface%20on%20laptop%20screen%20with%20colorful%20anime%20thumbnails%2C%20warm%20pink%20accent%20colors%2C%20minimalist%20UI%20design%2C%20clean%20simple%20background%2C%20professional%20product%20photography&width=800&height=500&seq=platform-card-04&orientation=landscape",
    5: "https://readdy.ai/api/search-image?query=Modern%20short%20video%20app%20interface%20on%20smartphone%20in%20vertical%20orientation%2C%20warm%20orange%20accent%20colors%2C%20minimalist%20design%2C%20clean%20simple%20background%2C%20professional%20product%20photography&width=800&height=500&seq=platform-card-05&orientation=landscape",
    6: "https://readdy.ai/api/search-image?query=Modern%20movie%20streaming%20platform%20interface%20on%20desktop%20monitor%20with%20film%20posters%2C%20warm%20indigo%20violet%20accent%20colors%2C%20minimalist%20design%2C%20clean%20simple%20background%2C%20professional%20product%20photography&width=800&height=500&seq=platform-card-06&orientation=landscape",
    7: "https://readdy.ai/api/search-image?query=Modern%20TV%20drama%20streaming%20interface%20on%20iPad%20with%20series%20thumbnails%2C%20warm%20cyan%20blue%20accent%20colors%2C%20minimalist%20design%2C%20clean%20simple%20background%2C%20professional%20product%20photography&width=800&height=500&seq=platform-card-07&orientation=landscape",
    8: "https://readdy.ai/api/search-image?query=Modern%20anime%20platform%20interface%20on%20smartphone%20with%20colorful%20character%20thumbnails%2C%20warm%20purple%20fuchsia%20accent%20colors%2C%20minimalist%20design%2C%20clean%20simple%20background%2C%20professional%20product%20photography&width=800&height=500&seq=platform-card-08&orientation=landscape",
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryMap[activeSlug] || '全部平台'} - 视频平台分类`,
    description: `浏览${categoryMap[activeSlug] || '全部'}视频平台，发现最适合您的在线视频观看平台。高清免费、流畅不卡顿。`,
    url: `https://hongfengyi.cn/category/${activeSlug}`,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '平台分类' },
              ]}
            />
          </div>
        </div>

        <section className="w-full py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                视频平台分类
              </h1>
              <p className="text-base text-foreground-600">
                按类别浏览，快速找到最适合你的视频平台
              </p>
            </div>

            <div className="flex items-center justify-center flex-wrap gap-2 mb-12" role="tablist">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  role="tab"
                  aria-selected={activeSlug === cat.slug}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`whitespace-nowrap px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                  <span className={`ml-1.5 text-xs ${activeSlug === cat.slug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                    ({cat.count})
                  </span>
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-product-shop>
              {filteredPlatforms.map((platform) => (
                <div
                  key={platform.id}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={platformImages[platform.id]}
                      alt={platform.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={`${platform.name} - 高清免费在线观看视频平台`}
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-background-50/90 text-foreground-800 whitespace-nowrap">
                        {platform.category}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-accent-500 text-background-50 whitespace-nowrap">
                        <i className="ri-star-fill text-xs"></i>
                        {platform.rating}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">
                      {platform.name}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">
                      {platform.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {platform.features.map((f) => (
                        <span
                          key={f}
                          className="px-2 py-0.5 rounded-md text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                    <a
                      href={platform.siteUrl}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="whitespace-nowrap inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      访问平台
                      <i className="ri-external-link-line"></i>
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {filteredPlatforms.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无平台</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}